import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {CacheType, CodeSpecNum} from "../../../constant/CodeSpecNum";
import {
    deleteCode,
    getFlatCodes,
    saveCode,
    selectCodesByParentId,
    updateCode,
    updateCodeName,
} from "../../../api/code/CodeService";
import {
    deleteServiceCode,
    saveServiceCode,
    selectServiceCodeByParentId,
    updateServiceCode,
    updateServiceCodeName,
} from "../../../api/serviceCode/ServiceCodeService";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import {changeRefreshContentStatus} from "../../../redux/slice/statusSlice";
import {setApprovalProcess, setApprovalProcessAfter} from "../../../redux/slice/contentSlice";
import {preloadApprovalProcessFromCodeEntry} from "../approval/shared/approvalProcessStore";
import {useCacheRefresh} from "../../../hooks/useCacheRefresh";

function mergeApprovalFields(code, source) {
    if (!code) {
        return code;
    }

    const approvalSource = source ?? code;
    return {
        ...code,
        approvalProcessId: approvalSource.approvalProcessId ?? code.approvalProcessId ?? code.approvalProcess?.id ?? null,
        approvalProcessIdAfter: approvalSource.approvalProcessIdAfter ?? code.approvalProcessIdAfter ?? code.approvalProcessAfter?.id ?? null,
        approvalProcess: approvalSource.approvalProcess ?? code.approvalProcess,
        approvalProcessAfter: approvalSource.approvalProcessAfter ?? code.approvalProcessAfter,
    };
}

export function useGroupCodeCategory(categoryMeta) {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const refresh = useSelector((state) => state.status.refreshContent);
    const approvalProcess = useSelector((state) => state.content.approvalProcess);
    const approvalProcessAfter = useSelector((state) => state.content.approvalProcessAfter);
    const refreshCache = useCacheRefresh();

    const [listData, setListData] = useState([]);
    const [flatCodes, setFlatCodes] = useState([]);
    const [selectedCode, setSelectedCode] = useState(null);
    const [serviceSubNav, setServiceSubNav] = useState("code");

    const enabled = !!categoryMeta;
    const rootCodeId = categoryMeta?.kind === "service"
        ? CodeSpecNum.ServiceRequestGroupCode
        : categoryMeta?.assetCodeType?.CodeSpecNum;

    const supportsApproval = !!(
        categoryMeta?.supportsApprovalLines
        || categoryMeta?.supportsApprovalBeforeOnly
        || categoryMeta?.kind === "service"
    );

    const loadCodeApprovalMeta = useCallback(async (code) => {
        if (!code?.id || !categoryMeta) {
            return code;
        }

        if (categoryMeta.kind === "service") {
            const enriched = mergeApprovalFields(code);
            preloadApprovalProcessFromCodeEntry(dispatch, enriched, {includeAfter: false});
            return enriched;
        }

        if (!supportsApproval || !rootCodeId) {
            return code;
        }

        try {
            const match = flatCodes.find((item) => item.id === code.id)
                ?? (await getFlatCodes(rootCodeId))?.data?.find((item) => item.id === code.id);
            if (match) {
                const enriched = mergeApprovalFields(code, match);
                preloadApprovalProcessFromCodeEntry(dispatch, enriched, {
                    includeAfter: !!categoryMeta.supportsApprovalLines,
                });
                return enriched;
            }
        } catch {
            // flat 조회 실패 시 트리 노드 데이터로 fallback
        }

        const enriched = mergeApprovalFields(code);
        preloadApprovalProcessFromCodeEntry(dispatch, enriched, {
            includeAfter: !!categoryMeta.supportsApprovalLines,
        });
        return enriched;
    }, [categoryMeta, dispatch, flatCodes, rootCodeId, supportsApproval]);

    const loadFlatCodes = useCallback(async (treeData) => {
        if (!supportsApproval || !rootCodeId || categoryMeta?.kind === "service") {
            setFlatCodes([]);
            return;
        }

        try {
            const response = await getFlatCodes(rootCodeId);
            if (response?.status === 200 && Array.isArray(response.data)) {
                setFlatCodes(response.data);
                return;
            }
        } catch {
            // flat API 실패 시 트리 데이터로 fallback
        }

        const flattenTree = (nodes, result = []) => {
            for (const node of nodes ?? []) {
                result.push(node);
                flattenTree(node.children, result);
            }
            return result;
        };
        setFlatCodes(flattenTree(treeData ?? []));
    }, [categoryMeta?.kind, rootCodeId, supportsApproval]);

    const loadList = useCallback(async () => {
        if (!categoryMeta) {
            return null;
        }
        if (categoryMeta.kind === "service") {
            const data = await selectServiceCodeByParentId(CodeSpecNum.ServiceRequestGroupCode);
            setListData(data ?? []);
            setFlatCodes([]);
            return data;
        }

        const data = await selectCodesByParentId(rootCodeId);
        setListData(data ?? []);
        await loadFlatCodes(data);
        return data;
    }, [categoryMeta, loadFlatCodes, rootCodeId]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        setSelectedCode(null);
        setFlatCodes([]);
        setServiceSubNav("code");
        dispatch(setApprovalProcess({}));
        dispatch(setApprovalProcessAfter({}));
        loadList();
    }, [categoryMeta?.id, dispatch, enabled, loadList]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        loadList();
    }, [enabled, loadList, refresh]);

    useEffect(() => {
        if (!enabled || !selectedCode?.id) {
            return;
        }
        setSelectedCode((prev) => {
            if (!prev) {
                return prev;
            }
            const findInTree = (nodes) => {
                for (const node of nodes ?? []) {
                    if (node.id === prev.id) {
                        return node;
                    }
                    const child = findInTree(node.children);
                    if (child) {
                        return child;
                    }
                }
                return null;
            };
            const next = findInTree(listData);
            return next ? mergeApprovalFields(next, prev) : null;
        });
    }, [enabled, listData, selectedCode?.id]);

    useEffect(() => {
        if (!enabled || !selectedCode?.id || !supportsApproval) {
            return undefined;
        }

        let cancelled = false;

        loadCodeApprovalMeta(selectedCode).then((enriched) => {
            if (!cancelled && enriched) {
                setSelectedCode(enriched);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [enabled, flatCodes, loadCodeApprovalMeta, selectedCode?.id, supportsApproval]);

    const handleCreateRoot = async (name) => {
        if (categoryMeta.kind === "service") {
            await saveServiceCode(
                {name, parentId: CodeSpecNum.ServiceRequestGroupCode},
                () => {
                    dispatch(changeRefreshContentStatus());
                    refreshCache(CacheType.serviceCode);
                    handleActiveToast({message: t("saved")});
                },
                () => handleActiveError({message: t("duplicatedNameExisted")}),
            );
            return;
        }

        await saveCode(
            {name, parentId: rootCodeId, rootCodeId},
            () => {
                dispatch(changeRefreshContentStatus());
                refreshCache(CacheType.code);
                handleActiveToast({message: t("saved")});
            },
            () => handleActiveError({message: t("duplicatedNameExisted")}),
        );
    };

    const handleCreateChild = async (parentId, name) => {
        if (categoryMeta.kind === "service") {
            await saveServiceCode(
                {name, parentId},
                () => {
                    dispatch(changeRefreshContentStatus());
                    refreshCache(CacheType.serviceCode);
                    handleActiveToast({message: t("saved")});
                },
                () => handleActiveError({message: t("duplicatedNameExisted")}),
            );
            return;
        }

        await saveCode(
            {name, parentId, rootCodeId},
            () => {
                dispatch(changeRefreshContentStatus());
                refreshCache(CacheType.code);
                handleActiveToast({message: t("saved")});
            },
            () => handleActiveError({message: t("duplicatedNameExisted")}),
        );
    };

    const handleDelete = async (codeId) => {
        const onSuccess = () => {
            if (selectedCode?.id === codeId) {
                setSelectedCode(null);
            }
            dispatch(changeRefreshContentStatus());
            handleActiveToast({message: t("removed")});
        };
        const onFailure = (data) => handleActiveError({
            message: "삭제할 수 없습니다.",
            description: t(`${data?.result}`),
        });

        if (categoryMeta.kind === "service") {
            refreshCache(CacheType.serviceCode);
            await deleteServiceCode(codeId, onSuccess, onFailure);
            return;
        }

        refreshCache(CacheType.code);
        await deleteCode(codeId, onSuccess, onFailure);
    };

    const handleUpdate = async () => {
        if (!selectedCode?.name?.trim()) {
            handleActiveToast({
                message: t("unableToSave.message"),
                description: t("unableToSave.description.HAS_NO_VALUE"),
            });
            return;
        }

        if (categoryMeta.kind === "service") {
            const saveData = {...selectedCode};
            if (Object.keys(approvalProcess).length > 0) {
                saveData.approvalProcessId = approvalProcess.id;
            } else {
                saveData.approvalProcessId = null;
            }

            await updateServiceCode(
                saveData,
                () => {
                    refreshCache(CacheType.serviceCode);
                    dispatch(changeRefreshContentStatus());
                    handleActiveToast({message: t("updated")});
                },
                () => handleActiveError({message: t("duplicatedNameExisted")}),
            );
            return;
        }

        const saveData = {...selectedCode};
        if (categoryMeta.supportsApprovalLines) {
            saveData.approvalProcessId = Object.keys(approvalProcess).length > 0 ? approvalProcess.id : null;
            saveData.approvalProcessIdAfter = Object.keys(approvalProcessAfter).length > 0 ? approvalProcessAfter.id : null;
        } else if (categoryMeta.supportsApprovalBeforeOnly) {
            saveData.approvalProcessId = Object.keys(approvalProcess).length > 0 ? approvalProcess.id : null;
        }

        await updateCode(
            saveData,
            () => {
                dispatch(changeRefreshContentStatus());
                refreshCache(CacheType.code);
                handleActiveToast({message: t("updated")});
            },
            () => handleActiveError({message: t("duplicatedNameExisted")}),
        );
    };

    const handleUpdateChild = async (item) => {
        if (categoryMeta.kind === "service") {
            await updateServiceCodeName(
                item,
                () => {
                    dispatch(changeRefreshContentStatus());
                    refreshCache(CacheType.serviceCode);
                    handleActiveToast({message: t("updated")});
                },
                () => handleActiveError({message: t("duplicatedNameExisted")}),
            );
            return;
        }

        await updateCodeName(
            {...item, rootCodeId},
            () => {
                dispatch(changeRefreshContentStatus());
                refreshCache(CacheType.code);
                handleActiveToast({message: t("updated")});
            },
            () => handleActiveError({message: t("duplicatedNameExisted")}),
        );
    };

    const handleSelectCode = (code) => {
        setSelectedCode(code);
        if (categoryMeta.kind === "service") {
            setServiceSubNav("code");
        }
    };

    return useMemo(() => {
        if (!enabled) {
            return null;
        }

        return {
            listData,
            selectedCode,
            setSelectedCode,
            serviceSubNav,
            setServiceSubNav,
            handleCreateRoot,
            handleCreateChild,
            handleDelete,
            handleUpdate,
            handleUpdateChild,
            handleUpdateChild,
            handleSelectCode,
        };
    }, [
        enabled,
        listData,
        selectedCode,
        serviceSubNav,
        handleCreateRoot,
        handleCreateChild,
        handleDelete,
        handleUpdate,
        handleUpdateChild,
        handleSelectCode,
    ]);
}
